/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'
import { Award, FolderOpen, Eye, EyeOff, DollarSign, ExternalLink, Layers, TrendingUp, Github } from 'lucide-react'
import { OPENROUTER_MODELS } from '@/providers/llm-provider'
import { useProjectStore } from '@/ui/stores/project-store'
import { JudgeLeaderboardModal } from './JudgeLeaderboardModal'
import { LLMLeaderboardModal } from './LLMLeaderboardModal'
import { getApiKey, saveApiKeys, type ApiKeyProvider } from '@/lib/api-keys'
import { getWorkspaceHandle, saveWorkspaceHandle } from '@/lib/storage'
import { TIER_DESCRIPTIONS, type ModelTier, type ModelOrigin, getModelsByTierAndOrigin } from '@/providers/model-tiers'
import { UsageMetrics } from './UsageMetrics'
import { ClaudeBridgePanel } from './ClaudeBridgePanel'

interface SettingsModalProps {
  onClose: () => void
  highPriority?: boolean // When true, renders above other modals (z-60 instead of z-50)
}

export function SettingsModal({ onClose, highPriority = false }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<'api-keys' | 'models' | 'workspace' | 'cost-savings' | 'model-tiers' | 'usage'>('models')
  const { currentProject, updateProjectSettings } = useProjectStore()
  const [showLeaderboard, setShowLeaderboard] = useState(false)
  const [showLLMLeaderboard, setShowLLMLeaderboard] = useState(false)
  const [llmLeaderboardCategory, setLLMLeaderboardCategory] = useState<'general' | 'coding' | 'all'>('all')

  const [models, setModels] = useState({
    orchestrator: currentProject?.settings?.models?.orchestrator || 'anthropic/claude-3-5-sonnet-20241022',
    judge: currentProject?.settings?.models?.judge || 'anthropic/claude-3-5-sonnet-20241022',
  })

  const [apiKeys, setApiKeys] = useState({
    openrouter: getApiKey('openrouter') || '',
    openai: getApiKey('openai') || '',
    anthropic: getApiKey('anthropic') || '',
    google: getApiKey('google') || '',
    xai: getApiKey('xai') || '',
    deepseek: getApiKey('deepseek') || '',
    groq: getApiKey('groq') || '',
    together: getApiKey('together') || '',
    siliconflow: getApiKey('siliconflow') || '',
    perplexity: getApiKey('perplexity') || '',
    tavily: getApiKey('tavily') || '',
    brave: getApiKey('brave') || '',
    serper: getApiKey('serper') || '',
    github: getApiKey('github') || '',
  })

  const [showApiKeys, setShowApiKeys] = useState({
    openrouter: false,
    openai: false,
    anthropic: false,
    google: false,
    xai: false,
    deepseek: false,
    groq: false,
    together: false,
    siliconflow: false,
    perplexity: false,
    tavily: false,
    brave: false,
    serper: false,
    github: false,
  })

  const [workspacePath, setWorkspacePath] = useState<string>('')

  // Model tier preferences
  const [tierPreferences, setTierPreferences] = useState<{
    preferredTier: ModelTier
    preferredOrigin: ModelOrigin | 'any'
    useTierForPeers: boolean
    useTierForJudge: boolean
    useTierForOrchestrator: boolean
  }>({
    preferredTier: currentProject?.settings?.modelTierPreferences?.preferredTier || 'ultra-cheap',
    preferredOrigin: currentProject?.settings?.modelTierPreferences?.preferredOrigin || 'any',
    useTierForPeers: currentProject?.settings?.modelTierPreferences?.useTierForPeers ?? true,
    useTierForJudge: currentProject?.settings?.modelTierPreferences?.useTierForJudge ?? false,
    useTierForOrchestrator: currentProject?.settings?.modelTierPreferences?.useTierForOrchestrator ?? false,
  })

  useEffect(() => {
    // Load workspace path on mount
    getWorkspaceHandle().then((handle) => {
      if (handle) {
        setWorkspacePath(handle.name)
      }
    })
  }, [])

  const handleSaveModels = async () => {
    if (!currentProject) return

    await updateProjectSettings({
      ...currentProject.settings,
      models,
    })

    onClose()
  }

  const handleSelectJudgeFromLeaderboard = (modelId: string) => {
    setModels({ ...models, judge: modelId })
  }

  const handleSaveApiKeys = async () => {
    // Filter out empty keys and save
    const keysToSave: Partial<Record<ApiKeyProvider, string>> = {}
    Object.entries(apiKeys).forEach(([provider, key]) => {
      if (key.trim()) {
        keysToSave[provider as ApiKeyProvider] = key.trim()
      }
    })

    console.log('💾 Saving API keys:', Object.keys(keysToSave))
    console.log('📋 Keys to save (obfuscated):', Object.fromEntries(
      Object.entries(keysToSave).map(([k, v]) => [k, v.slice(0, 8) + '...' + v.slice(-4)])
    ))

    await saveApiKeys(keysToSave)

    // Verify keys were saved
    console.log('✅ Verifying saved keys:')
    Object.keys(keysToSave).forEach(provider => {
      const retrieved = getApiKey(provider as ApiKeyProvider)
      console.log(`  ${provider}: ${retrieved ? '✓ Found (' + retrieved.slice(0, 8) + '...' + retrieved.slice(-4) + ')' : '✗ NOT FOUND'}`)
    })

    onClose()
  }

  const handleChangeWorkspace = async () => {
    try {
      // @ts-ignore - File System Access API
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents',
      })

      await saveWorkspaceHandle(dirHandle)
      setWorkspacePath(dirHandle.name)

      // Reload projects from new workspace
      useProjectStore.getState().loadProjects()
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Error selecting workspace:', err)
      }
    }
  }

  return (
    <>
      {showLeaderboard && (
        <JudgeLeaderboardModal
          onClose={() => setShowLeaderboard(false)}
          onSelectModel={handleSelectJudgeFromLeaderboard}
        />
      )}
      {showLLMLeaderboard && (
        <LLMLeaderboardModal
          onClose={() => setShowLLMLeaderboard(false)}
          onSelectModel={(modelId) => {
            setModels({ ...models, orchestrator: modelId })
          }}
          initialCategory={llmLeaderboardCategory}
        />
      )}
      <div
        className={`fixed inset-0 bg-black/50 flex items-center justify-center ${highPriority ? 'z-60' : 'z-50'}`}
        onClick={onClose}
      >
        <div
          className="bg-background-secondary border border-border rounded-lg w-[700px] max-h-[80vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-background-secondary border-b border-border p-4 flex items-center justify-between">
            <h2 className="text-lg font-title">Settings</h2>
            <button className="btn-ghost p-1" onClick={onClose}>
              <span className="text-xl">×</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-border flex">
            <button
              className={`px-4 py-2 text-sm font-condensed ${activeTab === 'models'
                ? 'border-b-2 border-accent text-foreground'
                : 'text-foreground-muted hover:text-foreground'
                }`}
              onClick={() => setActiveTab('models')}
            >
              Model Selection
            </button>
            <button
              className={`px-4 py-2 text-sm font-condensed ${activeTab === 'api-keys'
                ? 'border-b-2 border-accent text-foreground'
                : 'text-foreground-muted hover:text-foreground'
                }`}
              onClick={() => setActiveTab('api-keys')}
            >
              API Keys
            </button>
            <button
              className={`px-4 py-2 text-sm font-condensed flex items-center gap-1 ${activeTab === 'model-tiers'
                ? 'border-b-2 border-accent text-foreground'
                : 'text-foreground-muted hover:text-foreground'
                }`}
              onClick={() => setActiveTab('model-tiers')}
              title="Choose models by cost/quality tier"
            >
              <Layers className="w-4 h-4" />
              Model Tiers
            </button>
            <button
              className={`px-4 py-2 text-sm font-condensed flex items-center gap-1 ${activeTab === 'cost-savings'
                ? 'border-b-2 border-accent text-foreground'
                : 'text-foreground-muted hover:text-foreground'
                }`}
              onClick={() => setActiveTab('cost-savings')}
              title="Learn how to reduce API costs by 5-20x"
            >
              <DollarSign className="w-4 h-4" />
              Cost Savings
            </button>
            <button
              className={`px-4 py-2 text-sm font-condensed flex items-center gap-1 ${activeTab === 'usage'
                ? 'border-b-2 border-accent text-foreground'
                : 'text-foreground-muted hover:text-foreground'
                }`}
              onClick={() => setActiveTab('usage')}
              title="Monitor API usage and rate limits"
            >
              <TrendingUp className="w-4 h-4" />
              Usage
            </button>
            <button
              className={`px-4 py-2 text-sm font-condensed ${activeTab === 'workspace'
                ? 'border-b-2 border-accent text-foreground'
                : 'text-foreground-muted hover:text-foreground'
                }`}
              onClick={() => setActiveTab('workspace')}
            >
              Workspace
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-4">
            {activeTab === 'models' && (
              <div className="space-y-4">
                {/* Quick Tier Selection */}
                <div className="bg-accent/5 border border-accent/30 rounded p-3">
                  <h4 className="text-xs font-condensed text-foreground-secondary mb-2 flex items-center gap-1">
                    <Layers className="w-3 h-3" />
                    Quick Select by Tier (Updates All Models)
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        // Update orchestrator and judge
                        setModels({
                          orchestrator: 'groq/llama-3.3-70b-versatile',
                          judge: 'groq/llama-3.3-70b-versatile',
                        })
                        // Update all peer models
                        if (currentProject?.settings?.peers) {
                          const updatedPeers = currentProject.settings.peers.map(peer => ({
                            ...peer,
                            model: 'groq/llama-3.3-70b-versatile'
                          }))
                          updateProjectSettings({
                            ...currentProject.settings,
                            peers: updatedPeers,
                          })
                        }
                      }}
                      className="btn-secondary text-xs py-1 px-2"
                      title="Ultra-cheap US models: ~$0.20/M tokens (Groq Llama 3.3 70B - blazing fast!)"
                    >
                      💰 Ultra-Cheap (US)
                    </button>
                    <button
                      onClick={() => {
                        // Update orchestrator and judge
                        setModels({
                          orchestrator: 'deepseek/deepseek-chat',
                          judge: 'deepseek/deepseek-chat',
                        })
                        // Update all peer models
                        if (currentProject?.settings?.peers) {
                          const updatedPeers = currentProject.settings.peers.map(peer => ({
                            ...peer,
                            model: 'deepseek/deepseek-chat'
                          }))
                          updateProjectSettings({
                            ...currentProject.settings,
                            peers: updatedPeers,
                          })
                        }
                      }}
                      className="btn-secondary text-xs py-1 px-2"
                      title="Absolute cheapest: ~$0.02/M tokens (DeepSeek Chat V3)"
                    >
                      💸 Cheapest Overall
                    </button>
                    <button
                      onClick={() => {
                        // Update orchestrator and judge
                        setModels({
                          orchestrator: 'google/gemini-2.0-flash-exp',
                          judge: 'openai/gpt-4o-mini',
                        })
                        // Update all peer models to budget tier
                        if (currentProject?.settings?.peers) {
                          const updatedPeers = currentProject.settings.peers.map(peer => ({
                            ...peer,
                            model: 'openai/gpt-4o-mini'
                          }))
                          updateProjectSettings({
                            ...currentProject.settings,
                            peers: updatedPeers,
                          })
                        }
                      }}
                      className="btn-secondary text-xs py-1 px-2"
                      title="Budget tier: $0.15-$1.20/M tokens (Gemini Flash + GPT-4o Mini)"
                    >
                      🎯 Budget
                    </button>
                    <button
                      onClick={() => {
                        // Update orchestrator and judge
                        setModels({
                          orchestrator: 'google/gemini-2.5-pro',
                          judge: 'anthropic/claude-3-5-sonnet-20241022',
                        })
                        // Update all peer models to premium tier
                        if (currentProject?.settings?.peers) {
                          const updatedPeers = currentProject.settings.peers.map(peer => ({
                            ...peer,
                            model: 'anthropic/claude-3-5-sonnet-20241022'
                          }))
                          updateProjectSettings({
                            ...currentProject.settings,
                            peers: updatedPeers,
                          })
                        }
                      }}
                      className="btn-secondary text-xs py-1 px-2"
                      title="Premium quality at reasonable cost (Gemini 2.5 Pro + Claude 3.5 Sonnet)"
                    >
                      ⭐ Premium
                    </button>
                  </div>
                  <p className="text-xs text-foreground-muted mt-2">
                    Sets orchestrator, judge, and all peer models to the selected tier.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-title">Orchestrator Model</h3>
                    <button
                      onClick={() => {
                        setLLMLeaderboardCategory('general')
                        setShowLLMLeaderboard(true)
                      }}
                      className="btn-secondary px-3 py-1 text-xs flex items-center gap-1"
                    >
                      <TrendingUp className="w-3 h-3" />
                      View Leaderboard
                    </button>
                  </div>
                  <p className="text-xs text-foreground-muted mb-4">
                    The Orchestrator coordinates the workflow and synthesizes feedback.
                    <br />
                    <strong>Best:</strong> Large context windows (200K+ tokens), strong reasoning, good at synthesis.
                  </p>

                  <div>
                    <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                      🧠 Orchestrator (Workflow Coordinator)
                    </label>
                    <select
                      value={models.orchestrator}
                      onChange={(e) => setModels({ ...models, orchestrator: e.target.value })}
                      className="input w-full text-sm"
                    >
                      <optgroup label="💎 Premium - Best in Class ($3-15/M tokens)">
                        <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (200K, $3-15/M) - BEST OVERALL</option>
                        <option value="openai/gpt-4o">GPT-4o (128K, $2.50-10/M) - STABLE</option>
                        <option value="anthropic/claude-3-opus-20240229">Claude 3 Opus (200K, $15-75/M)</option>
                      </optgroup>
                      <optgroup label="⭐ Premium Affordable ($1-5/M tokens)">
                        <option value="google/gemini-3-pro-preview">Gemini 3 Pro (1M context, $2-12/M) - THINKING MODE 🧠</option>
                        <option value="google/gemini-3-pro-preview-11-2025">Gemini 3 Pro Nov 2025 (1M, $2-12/M) - LATEST</option>
                        <option value="google/gemini-3-pro-preview-11-2025-thinking">Gemini 3 Pro Thinking (1M, $2-12/M) - DEEP REASONING 🧠</option>
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro (2M context!, $1.25-5/M) - HUGE CONTEXT</option>
                        <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (200K, $3-15/M)</option>
                        <option value="openai/gpt-4o">GPT-4o (128K, $2.50-10/M)</option>
                        <option value="openai/gpt-4-turbo">GPT-4 Turbo (128K, $10-30/M)</option>
                        <option value="google/gemini-3-pro-high-context">Gemini 3 Pro High Context (1M, $4-18/M) - 200K+ contexts</option>
                        <option value="google/gemini-3-deep-think">Gemini 3 Deep Think (1M, $2-12/M) - EXPERIMENTAL 🔬</option>
                      </optgroup>
                      <optgroup label="💰 Budget - Great Value ($0.25-1/M tokens)">
                        <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash (1M context!, $0.30-1.20/M) - BEST BUDGET</option>
                        <option value="openai/gpt-4o-mini">GPT-4o Mini (128K, $0.15-0.60/M)</option>
                        <option value="anthropic/claude-3-haiku-20240307">Claude 3 Haiku (200K, $0.80-4/M)</option>
                      </optgroup>
                      <optgroup label="💸 Ultra-Cheap - US Based ($0.18-0.20/M tokens)">
                        <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B (128K, $0.20/M) - BLAZING FAST ⚡</option>
                        <option value="together/meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Together Llama 3.1 70B (131K, $0.18/M)</option>
                        <option value="groq/mixtral-8x7b-32768">Groq Mixtral 8x7B (32K, $0.20/M)</option>
                        <option value="groq/llama3-8b-8192">Groq Llama 3 8B (8K, $0.20/M) - ULTRA FAST ⚡⚡</option>
                      </optgroup>
                      <optgroup label="🔥 Ultra-Cheap - Absolute Lowest ($0.01-0.10/M tokens)">
                        <option value="deepseek/deepseek-chat">DeepSeek Chat V3 (64K, $0.014-0.028/M) - CHEAPEST</option>
                        <option value="qwen/qwen3-235b-a22b">Qwen 3 235B-A22B MoE (128K, $0.10/M) - THINKING MODE 🧠</option>
                        <option value="qwen/qwen3-30b-a3b">Qwen 3 30B-A3B MoE (128K, $0.08/M) - THINKING MODE 🧠</option>
                        <option value="qwen/qwen3-32b">Qwen 3 32B (128K, $0.09/M) - NEW 2025</option>
                        <option value="siliconflow/Qwen/Qwen2.5-72B-Instruct">SiliconFlow Qwen 2.5 72B (32K, $0.086/M)</option>
                        <option value="together/meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Together Llama 3.1 8B (131K, $0.06/M)</option>
                        <option value="siliconflow/Qwen/Qwen2.5-7B-Instruct">SiliconFlow Qwen 2.5 7B (32K, $0.05/M)</option>
                      </optgroup>
                      <optgroup label="🔓 Open-Source - Large Models">
                        <option value="meta-llama/llama-3.1-405b-instruct">Llama 3.1 405B (128K)</option>
                        <option value="meta-llama/llama-3.1-70b-instruct">Llama 3.1 70B (128K)</option>
                        <option value="qwen/qwen-2.5-72b-instruct">Qwen 2.5 72B (32K)</option>
                        <option value="qwen/qwen3-14b">Qwen 3 14B (128K) - NEW 2025</option>
                        <option value="qwen/qwen3-8b">Qwen 3 8B (128K) - NEW 2025</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-title">Peer Models</h3>
                    <button
                      onClick={() => {
                        setLLMLeaderboardCategory('all')
                        setShowLLMLeaderboard(true)
                      }}
                      className="btn-secondary px-3 py-1 text-xs flex items-center gap-1"
                    >
                      <TrendingUp className="w-3 h-3" />
                      View Leaderboard
                    </button>
                  </div>
                  <p className="text-xs text-foreground-muted mb-4">
                    Select which models to use for each peer persona.
                    <br />
                    <strong>Tip:</strong> Mix different tiers for cost/quality balance. Premium for critical analysis, budget for quick reviews.
                  </p>

                  <div className="space-y-3">
                    {currentProject?.settings?.peers && (currentProject.settings.peers?.length ?? 0) > 0 ? (
                      currentProject?.settings?.peers?.map((peer, idx) => (
                        <div key={peer.id}>
                          <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                            <span className={peer.color}>●</span> {peer.personaName}
                          </label>
                          <select
                            value={peer.model}
                            onChange={(e) => {
                              const peers = currentProject.settings?.peers || []
                              const updatedPeers = peers.map(p =>
                                p.id === peer.id ? { ...p, model: e.target.value } : p
                              )
                              updateProjectSettings({
                                ...currentProject.settings,
                                peers: updatedPeers,
                              })
                            }}
                            className="input w-full text-sm"
                          >
                            <optgroup label="💎 Premium - Best in Class ($3-15/M tokens)">
                              <option value="anthropic/claude-sonnet-4-5">Claude Sonnet 4.5 (200K, $3-15/M) - BEST OVERALL</option>
                              <option value="openai/gpt-5">GPT-5 (128K, $5-15/M)</option>
                              <option value="anthropic/claude-3-opus-20240229">Claude 3 Opus (200K, $15-75/M)</option>
                            </optgroup>
                            <optgroup label="⭐ Premium Affordable ($1-5/M tokens)">
                              <option value="google/gemini-3-pro-preview">Gemini 3 Pro (1M context, $2-12/M) - THINKING MODE 🧠</option>
                              <option value="google/gemini-3-pro-preview-11-2025">Gemini 3 Pro Nov 2025 (1M, $2-12/M) - LATEST</option>
                              <option value="google/gemini-3-pro-preview-11-2025-thinking">Gemini 3 Pro Thinking (1M, $2-12/M) - DEEP REASONING 🧠</option>
                              <option value="google/gemini-2.5-pro">Gemini 2.5 Pro (2M context!, $1.25-5/M)</option>
                              <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (200K, $3-15/M)</option>
                              <option value="openai/gpt-4o">GPT-4o (128K, $2.50-10/M)</option>
                              <option value="openai/gpt-4-turbo">GPT-4 Turbo (128K, $10-30/M)</option>
                              <option value="google/gemini-3-pro-high-context">Gemini 3 Pro High Context (1M, $4-18/M) - 200K+ contexts</option>
                              <option value="google/gemini-3-deep-think">Gemini 3 Deep Think (1M, $2-12/M) - EXPERIMENTAL 🔬</option>
                            </optgroup>
                            <optgroup label="💰 Budget - Great Value ($0.15-1/M tokens)">
                              <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash (1M context!, $0.30-1.20/M) - BEST BUDGET</option>
                              <option value="openai/gpt-4o-mini">GPT-4o Mini (128K, $0.15-0.60/M)</option>
                              <option value="anthropic/claude-3-haiku-20240307">Claude 3 Haiku (200K, $0.80-4/M)</option>
                            </optgroup>
                            <optgroup label="💸 Ultra-Cheap - US Based ($0.18-0.20/M tokens)">
                              <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B (128K, $0.20/M) - BLAZING FAST ⚡</option>
                              <option value="together/meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Together Llama 3.1 70B (131K, $0.18/M)</option>
                              <option value="groq/mixtral-8x7b-32768">Groq Mixtral 8x7B (32K, $0.20/M)</option>
                              <option value="groq/llama3-8b-8192">Groq Llama 3 8B (8K, $0.20/M) - ULTRA FAST ⚡⚡</option>
                            </optgroup>
                            <optgroup label="🔥 Ultra-Cheap - Absolute Lowest ($0.01-0.10/M tokens)">
                              <option value="deepseek/deepseek-chat">DeepSeek Chat V3 (64K, $0.014-0.028/M) - CHEAPEST 💰</option>
                              <option value="qwen/qwen3-235b-a22b">Qwen 3 235B-A22B MoE (128K, $0.10/M) - THINKING MODE 🧠</option>
                              <option value="qwen/qwen3-30b-a3b">Qwen 3 30B-A3B MoE (128K, $0.08/M) - THINKING MODE 🧠</option>
                              <option value="qwen/qwen3-32b">Qwen 3 32B (128K, $0.09/M) - NEW 2025</option>
                              <option value="siliconflow/Qwen/Qwen2.5-72B-Instruct">SiliconFlow Qwen 2.5 72B (32K, $0.086/M)</option>
                              <option value="together/meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Together Llama 3.1 8B (131K, $0.06/M)</option>
                              <option value="siliconflow/Qwen/Qwen2.5-7B-Instruct">SiliconFlow Qwen 2.5 7B (32K, $0.05/M)</option>
                            </optgroup>
                            <optgroup label="🔓 Open-Source - Large Models">
                              <option value="meta-llama/llama-3.1-405b-instruct">Llama 3.1 405B (128K)</option>
                              <option value="meta-llama/llama-3.1-70b-instruct">Llama 3.1 70B (128K)</option>
                              <option value="qwen/qwen-2.5-72b-instruct">Qwen 2.5 72B (32K)</option>
                              <option value="qwen/qwen3-14b">Qwen 3 14B (128K) - NEW 2025</option>
                              <option value="qwen/qwen3-8b">Qwen 3 8B (128K) - NEW 2025</option>
                            </optgroup>
                            <optgroup label="🌐 All Models (Alphabetical)">
                              {OPENROUTER_MODELS.map((model) => (
                                <option key={model.id} value={model.id}>
                                  {model.name} ({model.provider})
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                      ))
                    ) : (
                      <div className="bg-background-tertiary border border-border rounded p-4 text-center">
                        <p className="text-sm text-foreground-muted mb-2">
                          No peers added yet
                        </p>
                        <p className="text-xs text-foreground-muted">
                          Click the <strong>+ Add Peer</strong> button in the Peer Review panel to add your first peer reviewer.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-title">Judge Model</h3>
                    <button
                      onClick={() => setShowLeaderboard(true)}
                      className="btn-secondary px-3 py-1 text-xs flex items-center gap-1"
                    >
                      <Award className="w-3 h-3" />
                      View Leaderboard
                    </button>
                  </div>
                  <p className="text-xs text-foreground-muted mb-4">
                    The Judge evaluates all peer responses and provides a quality score.
                    <br />
                    <strong>Best:</strong> Strong reasoning, good at evaluation, consistent scoring. Context window less critical.
                  </p>

                  <div>
                    <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                      🟡 Judge (Quality Assessor)
                    </label>
                    <select
                      value={models.judge}
                      onChange={(e) => setModels({ ...models, judge: e.target.value })}
                      className="input w-full text-sm"
                    >
                      <optgroup label="💎 Premium - Best Judges ($3-15/M tokens)">
                        <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet ($3-15/M) - BEST JUDGE</option>
                        <option value="openai/gpt-4o">GPT-4o ($2.50-10/M) - STABLE</option>
                        <option value="openai/gpt-4-turbo">GPT-4 Turbo ($10-30/M)</option>
                        <option value="anthropic/claude-3-opus-20240229">Claude 3 Opus ($15-75/M)</option>
                      </optgroup>
                      <optgroup label="⭐ Premium Affordable ($1-5/M tokens)">
                        <option value="google/gemini-2.5-pro">Gemini 2.5 Pro ($1.25-5/M) - RECOMMENDED</option>
                        <option value="openai/gpt-4o">GPT-4o ($2.50-10/M)</option>
                        <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet ($3-15/M)</option>
                      </optgroup>
                      <optgroup label="💰 Budget - Good Judges ($0.15-1/M tokens)">
                        <option value="openai/gpt-4o-mini">GPT-4o Mini ($0.15-0.60/M) - BEST BUDGET JUDGE</option>
                        <option value="anthropic/claude-3-haiku-20240307">Claude 3 Haiku ($0.80-4/M)</option>
                        <option value="google/gemini-2.0-flash-exp">Gemini 2.0 Flash ($0.30-1.20/M)</option>
                      </optgroup>
                      <optgroup label="💸 Ultra-Cheap - US Based ($0.18-0.20/M tokens)">
                        <option value="groq/llama-3.3-70b-versatile">Groq Llama 3.3 70B (128K, $0.20/M) - BLAZING FAST ⚡</option>
                        <option value="together/meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Together Llama 3.1 70B (131K, $0.18/M)</option>
                        <option value="groq/mixtral-8x7b-32768">Groq Mixtral 8x7B (32K, $0.20/M)</option>
                        <option value="groq/llama3-8b-8192">Groq Llama 3 8B (8K, $0.20/M) - ULTRA FAST ⚡⚡</option>
                      </optgroup>
                      <optgroup label="🔥 Ultra-Cheap - Absolute Lowest ($0.01-0.10/M tokens)">
                        <option value="deepseek/deepseek-chat">DeepSeek Chat V3 ($0.014-0.028/M) - CHEAPEST</option>
                        <option value="qwen/qwen3-235b-a22b">Qwen 3 235B-A22B MoE ($0.10/M) - THINKING MODE 🧠</option>
                        <option value="qwen/qwen3-30b-a3b">Qwen 3 30B-A3B MoE ($0.08/M) - THINKING MODE 🧠</option>
                        <option value="qwen/qwen3-32b">Qwen 3 32B ($0.09/M) - NEW 2025</option>
                        <option value="siliconflow/Qwen/Qwen2.5-72B-Instruct">SiliconFlow Qwen 2.5 72B ($0.086/M)</option>
                        <option value="together/meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Together Llama 3.1 8B ($0.06/M)</option>
                        <option value="siliconflow/Qwen/Qwen2.5-7B-Instruct">SiliconFlow Qwen 2.5 7B ($0.05/M)</option>
                      </optgroup>
                      <optgroup label="🔓 Open-Source - Large Models">
                        <option value="meta-llama/llama-3.1-405b-instruct">Llama 3.1 405B (Meta)</option>
                        <option value="meta-llama/llama-3.1-70b-instruct">Llama 3.1 70B (Meta)</option>
                        <option value="qwen/qwen-2.5-72b-instruct">Qwen 2.5 72B (Qwen)</option>
                        <option value="qwen/qwen3-14b">Qwen 3 14B (Qwen) - NEW 2025</option>
                        <option value="qwen/qwen3-8b">Qwen 3 8B (Qwen) - NEW 2025</option>
                        <option value="qwen/qwen-2.5-14b-instruct">Qwen 2.5 14B (Qwen)</option>
                      </optgroup>
                      <optgroup label="🌐 All Models">
                        {OPENROUTER_MODELS.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <button className="btn-secondary px-4 py-2" onClick={onClose}>
                    Cancel
                  </button>
                  <button className="btn-primary px-4 py-2" onClick={handleSaveModels}>
                    Save Models
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'api-keys' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-title mb-2">API Keys</h3>
                  <p className="text-xs text-foreground-muted mb-4">
                    Configure API keys for LLM providers. For local testing, you can also set these in <code className="code-block px-1">.env</code>
                  </p>

                  {/* Privacy Notice */}
                  <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xs font-medium text-accent mb-1">🔒 Your Privacy is Protected</h4>
                        <p className="text-xs text-foreground-muted leading-relaxed">
                          All API keys are stored <strong>locally in your browser</strong> using localStorage and IndexedDB.
                          Your keys are <strong>never sent to any server</strong> or stored outside of your device.
                          HiveMind is a 100% client-side application - your API keys go directly from your browser to the LLM providers you choose.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                        OpenRouter API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.openrouter ? 'text' : 'password'}
                          placeholder="sk-or-..."
                          value={apiKeys.openrouter}
                          onChange={(e) => setApiKeys({ ...apiKeys, openrouter: e.target.value })}
                          className="input w-full text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys({ ...showApiKeys, openrouter: !showApiKeys.openrouter })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                          title={showApiKeys.openrouter ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKeys.openrouter ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-foreground-muted mt-1">Env: VITE_OPENROUTER_API_KEY</p>
                    </div>

                    <div>
                      <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                        OpenAI API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.openai ? 'text' : 'password'}
                          placeholder="sk-..."
                          value={apiKeys.openai}
                          onChange={(e) => setApiKeys({ ...apiKeys, openai: e.target.value })}
                          className="input w-full text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys({ ...showApiKeys, openai: !showApiKeys.openai })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                          title={showApiKeys.openai ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKeys.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-foreground-muted mt-1">Env: VITE_OPENAI_API_KEY</p>
                    </div>

                    <div>
                      <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                        Anthropic API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.anthropic ? 'text' : 'password'}
                          placeholder="sk-ant-..."
                          value={apiKeys.anthropic}
                          onChange={(e) => setApiKeys({ ...apiKeys, anthropic: e.target.value })}
                          className="input w-full text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys({ ...showApiKeys, anthropic: !showApiKeys.anthropic })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                          title={showApiKeys.anthropic ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKeys.anthropic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-foreground-muted mt-1">Env: VITE_ANTHROPIC_API_KEY</p>
                      <ClaudeBridgePanel />
                    </div>

                    <div>
                      <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                        Google AI API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.google ? 'text' : 'password'}
                          placeholder="AIza..."
                          value={apiKeys.google}
                          onChange={(e) => setApiKeys({ ...apiKeys, google: e.target.value })}
                          className="input w-full text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys({ ...showApiKeys, google: !showApiKeys.google })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                          title={showApiKeys.google ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKeys.google ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-foreground-muted mt-1">Env: VITE_GOOGLE_API_KEY</p>
                    </div>

                    <div>
                      <label className="block text-xs font-condensed text-foreground-secondary mb-1">
                        xAI (Grok) API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.xai ? 'text' : 'password'}
                          placeholder="xai-..."
                          value={apiKeys.xai}
                          onChange={(e) => setApiKeys({ ...apiKeys, xai: e.target.value })}
                          className="input w-full text-sm pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys({ ...showApiKeys, xai: !showApiKeys.xai })}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                          title={showApiKeys.xai ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKeys.xai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-foreground-muted mt-1">Env: VITE_XAI_API_KEY</p>
                    </div>
                  </div>

                  <div className="border-t border-accent/30 pt-4 mt-4">
                    <h4 className="text-sm font-title mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-accent" />
                      Ultra-Low Cost Providers (Recommended)
                    </h4>
                    <p className="text-xs text-foreground-muted mb-3">
                      These providers offer 5-20x lower costs than OpenRouter. <button onClick={() => setActiveTab('cost-savings')} className="text-accent hover:underline">Learn more →</button>
                    </p>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          DeepSeek API Key
                          <span className="text-accent text-[10px]">($0.01-$0.05/M tokens)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.deepseek ? 'text' : 'password'}
                            placeholder="sk-..."
                            value={apiKeys.deepseek}
                            onChange={(e) => setApiKeys({ ...apiKeys, deepseek: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, deepseek: !showApiKeys.deepseek })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.deepseek ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.deepseek ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_DEEPSEEK_API_KEY</p>
                          <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          Groq API Key
                          <span className="text-accent text-[10px]">(~$0.20/M tokens)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.groq ? 'text' : 'password'}
                            placeholder="gsk_..."
                            value={apiKeys.groq}
                            onChange={(e) => setApiKeys({ ...apiKeys, groq: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, groq: !showApiKeys.groq })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.groq ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.groq ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_GROQ_API_KEY</p>
                          <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          Together AI API Key
                          <span className="text-green-400 text-[10px]">(Free tier available)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.together ? 'text' : 'password'}
                            placeholder="..."
                            value={apiKeys.together}
                            onChange={(e) => setApiKeys({ ...apiKeys, together: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, together: !showApiKeys.together })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.together ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.together ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_TOGETHER_API_KEY</p>
                          <a href="https://api.together.xyz/signup" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          SiliconFlow API Key
                          <span className="text-accent text-[10px]">($0.05-$0.086/M tokens)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.siliconflow ? 'text' : 'password'}
                            placeholder="sk-..."
                            value={apiKeys.siliconflow}
                            onChange={(e) => setApiKeys({ ...apiKeys, siliconflow: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, siliconflow: !showApiKeys.siliconflow })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.siliconflow ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.siliconflow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_SILICONFLOW_API_KEY</p>
                          <a href="https://siliconflow.com" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          Perplexity API Key
                          <span className="text-accent text-[10px]">(Sonar models with built-in search)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.perplexity ? 'text' : 'password'}
                            placeholder="pplx-..."
                            value={apiKeys.perplexity}
                            onChange={(e) => setApiKeys({ ...apiKeys, perplexity: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, perplexity: !showApiKeys.perplexity })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.perplexity ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.perplexity ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_PERPLEXITY_API_KEY</p>
                          <a href="https://www.perplexity.ai/account/api/group" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-accent/30 pt-4 mt-4">
                    <h4 className="text-sm font-title mb-2 flex items-center gap-2">
                      🌐 Web Search APIs (Optional)
                    </h4>
                    <p className="text-xs text-foreground-muted mb-3">
                      Enable web search for models via function/tool calling. Required for GPT-4, Claude, Gemini web search.
                    </p>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          Tavily API Key
                          <span className="text-green-400 text-[10px]">(Free tier + AI-optimized)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.tavily ? 'text' : 'password'}
                            placeholder="tvly-..."
                            value={apiKeys.tavily}
                            onChange={(e) => setApiKeys({ ...apiKeys, tavily: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, tavily: !showApiKeys.tavily })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.tavily ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.tavily ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_TAVILY_API_KEY</p>
                          <a href="https://tavily.com" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          Brave Search API Key
                          <span className="text-accent text-[10px]">($5-$9/1k requests)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.brave ? 'text' : 'password'}
                            placeholder="BSA..."
                            value={apiKeys.brave}
                            onChange={(e) => setApiKeys({ ...apiKeys, brave: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, brave: !showApiKeys.brave })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.brave ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.brave ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_BRAVE_API_KEY</p>
                          <a href="https://brave.com/search/api/" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          Serper API Key
                          <span className="text-accent text-[10px]">($0.30/1k queries - Cheapest)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.serper ? 'text' : 'password'}
                            placeholder="..."
                            value={apiKeys.serper}
                            onChange={(e) => setApiKeys({ ...apiKeys, serper: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, serper: !showApiKeys.serper })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.serper ? 'Hide API key' : 'Show API key'}
                          >
                            {showApiKeys.serper ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_SERPER_API_KEY</p>
                          <a href="https://serper.dev" target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            Get Key
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* GitHub PAT */}
                    <div className="card p-4">
                      <h3 className="text-sm font-condensed font-semibold mb-3 flex items-center gap-2">
                        <Github className="w-4 h-4" />
                        GitHub Integration
                      </h3>
                      <div>
                        <label className="block text-xs font-condensed text-foreground-secondary mb-1 flex items-center gap-1">
                          GitHub Personal Access Token (PAT)
                          <span className="text-accent text-[10px]">(Clone & browse your repos)</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.github ? 'text' : 'password'}
                            placeholder="ghp_..."
                            value={apiKeys.github}
                            onChange={(e) => setApiKeys({ ...apiKeys, github: e.target.value })}
                            className="input w-full text-sm pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys({ ...showApiKeys, github: !showApiKeys.github })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-background-tertiary rounded transition-colors"
                            title={showApiKeys.github ? 'Hide PAT' : 'Show PAT'}
                          >
                            {showApiKeys.github ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-foreground-muted">Env: VITE_GITHUB_PAT</p>
                          <a
                            href="https://github.com/settings/tokens/new?description=HiveMind&scopes=repo"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-accent hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Create PAT
                          </a>
                        </div>
                        <p className="text-[10px] text-foreground-muted mt-1">Requires 'repo' scope to access private repositories</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <button className="btn-secondary px-4 py-2" onClick={onClose}>
                    Cancel
                  </button>
                  <button className="btn-primary px-4 py-2" onClick={handleSaveApiKeys}>
                    Save API Keys
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'model-tiers' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-title mb-2 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-accent" />
                    Choose Models by Cost/Quality Tier
                  </h3>
                  <p className="text-xs text-foreground-muted mb-4 leading-relaxed">
                    Select your preferred cost/quality tier and HiveMind will automatically choose appropriate models. Perfect for balancing cost and performance.
                  </p>
                </div>

                {/* Tier Selection */}
                <div>
                  <label className="block text-xs font-condensed text-foreground-secondary mb-2">
                    Preferred Tier
                  </label>
                  <div className="space-y-2">
                    {(Object.keys(TIER_DESCRIPTIONS) as ModelTier[]).map((tier) => {
                      const desc = TIER_DESCRIPTIONS[tier]
                      const isSelected = tierPreferences.preferredTier === tier
                      return (
                        <button
                          key={tier}
                          onClick={() => setTierPreferences({ ...tierPreferences, preferredTier: tier })}
                          className={`w-full text-left p-3 rounded border transition-colors ${isSelected
                            ? 'border-accent bg-accent/10'
                            : 'border-border hover:border-accent/50'
                            }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-condensed text-foreground">
                                  {desc.name}
                                </span>
                                <span className="text-xs text-accent">{desc.priceRange}</span>
                              </div>
                              <p className="text-xs text-foreground-muted">{desc.description}</p>
                            </div>
                            {isSelected && (
                              <span className="text-accent text-lg ml-2">✓</span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Origin Preference */}
                <div>
                  <label className="block text-xs font-condensed text-foreground-secondary mb-2">
                    Model Origin Preference
                  </label>
                  <select
                    value={tierPreferences.preferredOrigin}
                    onChange={(e) => setTierPreferences({ ...tierPreferences, preferredOrigin: e.target.value as ModelOrigin | 'any' })}
                    className="input w-full text-sm"
                  >
                    <option value="any">Any (Cheapest Available)</option>
                    <option value="us">🇺🇸 US-Based Only (Domestic Models)</option>
                    <option value="eu">🇪🇺 EU-Based Only</option>
                    <option value="china">🇨🇳 China-Based (Often Cheapest)</option>
                    <option value="international">🌍 International</option>
                  </select>
                  <p className="text-xs text-foreground-muted mt-1">
                    {tierPreferences.preferredOrigin === 'us' && 'Recommended for users who prefer US-based models (OpenAI, Anthropic, Google, Groq)'}
                    {tierPreferences.preferredOrigin === 'china' && 'DeepSeek and Qwen models offer the lowest costs but are China-based'}
                    {tierPreferences.preferredOrigin === 'any' && 'Automatically selects the cheapest model in your tier regardless of origin'}
                    {tierPreferences.preferredOrigin === 'eu' && 'Mistral and other EU-based models'}
                    {tierPreferences.preferredOrigin === 'international' && 'Models from various international providers'}
                  </p>
                </div>

                {/* Auto-Apply Settings */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-xs font-condensed text-foreground-secondary mb-3">
                    Auto-Apply Tier to:
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tierPreferences.useTierForPeers}
                        onChange={(e) => setTierPreferences({ ...tierPreferences, useTierForPeers: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Peer Models (Recommended)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tierPreferences.useTierForJudge}
                        onChange={(e) => setTierPreferences({ ...tierPreferences, useTierForJudge: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Judge Model</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tierPreferences.useTierForOrchestrator}
                        onChange={(e) => setTierPreferences({ ...tierPreferences, useTierForOrchestrator: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Orchestrator Model</span>
                    </label>
                  </div>
                  <p className="text-xs text-foreground-muted mt-3">
                    When enabled, HiveMind will automatically select models from your preferred tier when creating new peers or changing settings.
                  </p>
                </div>

                {/* Preview */}
                <div className="border border-accent/30 rounded p-3 bg-accent/5">
                  <h4 className="text-xs font-condensed text-foreground-secondary mb-2">
                    Preview: Recommended Models for Your Tier
                  </h4>
                  <div className="space-y-1 text-xs">
                    {(() => {
                      const models = getModelsByTierAndOrigin(
                        tierPreferences.preferredTier,
                        tierPreferences.preferredOrigin === 'any' ? undefined : tierPreferences.preferredOrigin as ModelOrigin
                      )
                      const topModels = models.filter(m => m.recommended).slice(0, 3)
                      if (topModels.length === 0) {
                        return <p className="text-foreground-muted">No models found for this tier/origin combination</p>
                      }
                      return topModels.map(m => (
                        <div key={m.id} className="flex items-center justify-between">
                          <span className="text-foreground">{m.name}</span>
                          <span className="text-accent">${m.costPerMTokens.input.toFixed(2)}-${m.costPerMTokens.output.toFixed(2)}/M</span>
                        </div>
                      ))
                    })()}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={async () => {
                      if (!currentProject) return
                      await updateProjectSettings({
                        ...currentProject.settings,
                        modelTierPreferences: tierPreferences,
                      })
                      onClose()
                    }}
                    className="btn-primary"
                  >
                    Save Tier Preferences
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'cost-savings' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-title mb-2 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-accent" />
                    Reduce API Costs by 5-20x
                  </h3>
                  <p className="text-xs text-foreground-muted mb-4 leading-relaxed">
                    OpenRouter adds markup fees to all API calls. Switch to direct API providers to dramatically reduce costs while maintaining access to high-quality models.
                  </p>

                  <div className="bg-background-tertiary border border-accent/30 rounded p-3 mb-4">
                    <h4 className="text-xs font-condensed text-accent mb-2">💡 Recommended: Ultra-Low Cost Providers</h4>
                    <p className="text-xs text-foreground-muted leading-relaxed">
                      These providers offer excellent performance at a fraction of OpenRouter's cost. Most have free tiers or signup bonuses!
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* DeepSeek */}
                    <div className="border border-border rounded p-3 hover:border-accent/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-condensed text-foreground">DeepSeek</h4>
                          <p className="text-xs text-accent">$0.01-$0.05 per million tokens</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-accent/20 text-accent rounded">Recommended</span>
                      </div>
                      <p className="text-xs text-foreground-muted mb-2">
                        Excellent for programming, research, and reasoning tasks. DeepSeek V3 rivals GPT-4 at 1/100th the cost.
                      </p>
                      <a
                        href="https://platform.deepseek.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Get API Key
                      </a>
                    </div>

                    {/* Groq */}
                    <div className="border border-border rounded p-3 hover:border-accent/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-condensed text-foreground">Groq</h4>
                          <p className="text-xs text-accent">~$0.20 per million tokens</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-accent/20 text-accent rounded">Blazing Fast</span>
                      </div>
                      <p className="text-xs text-foreground-muted mb-2">
                        Ultra-fast inference with Mixtral and Llama models. Perfect for real-time applications.
                      </p>
                      <a
                        href="https://console.groq.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Get API Key
                      </a>
                    </div>

                    {/* Together AI */}
                    <div className="border border-border rounded p-3 hover:border-accent/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-condensed text-foreground">Together AI</h4>
                          <p className="text-xs text-accent">Free tier + $0.20/M tokens</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Free Tier</span>
                      </div>
                      <p className="text-xs text-foreground-muted mb-2">
                        Access to Llama, Mixtral, Gemma, and more open models. Great free tier for testing.
                      </p>
                      <a
                        href="https://api.together.xyz/signup"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Get API Key
                      </a>
                    </div>

                    {/* SiliconFlow */}
                    <div className="border border-border rounded p-3 hover:border-accent/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-condensed text-foreground">SiliconFlow</h4>
                          <p className="text-xs text-accent">$0.05-$0.086 per million tokens</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-accent/20 text-accent rounded">Recommended</span>
                      </div>
                      <p className="text-xs text-foreground-muted mb-2">
                        Qwen, Llama, and GLM models with OpenAI-compatible API. Excellent for multilingual tasks.
                      </p>
                      <a
                        href="https://siliconflow.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Get API Key
                      </a>
                    </div>

                    {/* Google AI */}
                    <div className="border border-border rounded p-3 hover:border-accent/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-condensed text-foreground">Google AI Studio</h4>
                          <p className="text-xs text-accent">$0.30-$2.50 per million tokens</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Free Quota</span>
                      </div>
                      <p className="text-xs text-foreground-muted mb-2">
                        Gemini 2.5 Flash and Pro models. Free quota available. Great for multimodal tasks.
                      </p>
                      <a
                        href="https://aistudio.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Get API Key
                      </a>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-background-tertiary border border-border rounded">
                    <h4 className="text-xs font-condensed text-foreground-secondary mb-2">📊 Cost Comparison Example</h4>
                    <div className="text-xs text-foreground-muted space-y-1">
                      <div className="flex justify-between">
                        <span>1M tokens via OpenRouter (Claude Sonnet):</span>
                        <span className="text-red-400">~$4.50</span>
                      </div>
                      <div className="flex justify-between">
                        <span>1M tokens via DeepSeek V3:</span>
                        <span className="text-green-400">~$0.03</span>
                      </div>
                      <div className="flex justify-between font-condensed pt-1 border-t border-border mt-1">
                        <span>Savings:</span>
                        <span className="text-accent">150x cheaper!</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-accent/10 border border-accent/30 rounded">
                    <h4 className="text-xs font-condensed text-accent mb-2">🎯 How to Use</h4>
                    <ol className="text-xs text-foreground-muted space-y-1 leading-relaxed list-decimal list-inside">
                      <li>Click the links above to sign up for cheaper providers</li>
                      <li>Get your API keys from each provider</li>
                      <li>Add them in the "API Keys" tab</li>
                      <li>HiveMind will automatically use direct APIs when available (bypassing OpenRouter)</li>
                    </ol>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <button className="btn-secondary px-4 py-2" onClick={onClose}>
                    Close
                  </button>
                  <button
                    className="btn-primary px-4 py-2"
                    onClick={() => setActiveTab('api-keys')}
                  >
                    Add API Keys →
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'usage' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-title mb-2">API Usage Tracking</h3>
                  <p className="text-xs text-foreground-muted mb-4">
                    Monitor your API usage to stay within free tier limits and avoid unexpected charges.
                  </p>
                </div>

                {/* Google AI Studio Free Tier Promotion */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🆓</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-green-500 mb-1">
                        Get Started Free with Google AI Studio
                      </h4>
                      <p className="text-xs text-foreground-muted mb-3">
                        Google offers a generous free tier with <strong>1,500 requests per day</strong> for Gemini models.
                        Perfect for getting started with HiveMind at zero cost!
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href="https://aistudio.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary text-xs py-1 px-3 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Get Free API Key
                        </a>
                        <button
                          onClick={() => setActiveTab('api-keys')}
                          className="btn-secondary text-xs py-1 px-3"
                        >
                          Add API Key
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Metrics */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold">Current Usage</h4>

                  {apiKeys.google && (
                    <UsageMetrics provider="google" />
                  )}

                  {apiKeys.openai && (
                    <UsageMetrics provider="openai" />
                  )}

                  {apiKeys.anthropic && (
                    <UsageMetrics provider="anthropic" />
                  )}

                  {!apiKeys.google && !apiKeys.openai && !apiKeys.anthropic && (
                    <div className="text-center py-8 text-foreground-muted">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No API keys configured yet</p>
                      <p className="text-xs mt-1">Add an API key to start tracking usage</p>
                    </div>
                  )}
                </div>

                {/* Free Tier Limits Reference */}
                <div className="bg-background-tertiary border border-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-3">Free Tier Limits</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Google AI Studio:</span>
                      <span className="font-mono">15 RPM / 1,500 RPD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">OpenAI (Free Trial):</span>
                      <span className="font-mono">3 RPM / 200 RPD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-muted">Anthropic (Free Trial):</span>
                      <span className="font-mono">5 RPM / 1,000 RPD</span>
                    </div>
                    <div className="text-foreground-muted mt-3 pt-3 border-t border-border">
                      <p className="text-xs">
                        RPM = Requests Per Minute, RPD = Requests Per Day
                      </p>
                      <p className="text-xs mt-1">
                        Upgrade to paid tiers for higher limits and better performance.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'workspace' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-title mb-2">Workspace Directory</h3>
                  <p className="text-xs text-foreground-muted mb-4 leading-relaxed">
                    Choose where HiveMind stores all your projects. Each project will be created as a subfolder with its own <code className="bg-background text-accent px-1 py-0.5 rounded text-xs">.hivemind/</code> configuration folder.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-condensed text-foreground-secondary mb-2">
                        Current Workspace
                      </label>
                      {workspacePath ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0 px-3 py-2 bg-background-tertiary border border-border rounded text-sm truncate">
                            <FolderOpen className="w-4 h-4 inline mr-2 text-accent flex-shrink-0" />
                            <span className="truncate">{workspacePath}</span>
                          </div>
                          <button
                            onClick={handleChangeWorkspace}
                            className="btn-secondary px-3 py-2 text-sm flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="px-3 py-2 bg-background-tertiary border border-border rounded text-sm text-foreground-muted">
                            No workspace set up (using browser-only mode)
                          </div>
                          <button
                            onClick={handleChangeWorkspace}
                            className="btn-primary px-3 py-2 text-sm flex items-center gap-2"
                          >
                            <FolderOpen className="w-4 h-4" />
                            Select Workspace Folder
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="pt-3 border-t border-border">
                      <h4 className="text-xs font-condensed text-foreground-secondary mb-2">How it works</h4>
                      <ul className="text-xs text-foreground-muted space-y-1.5 leading-relaxed">
                        <li>• Projects are stored as folders: <code className="bg-background text-accent px-1 py-0.5 rounded text-xs whitespace-nowrap">workspace/project-name/</code></li>
                        <li>• Each project has a <code className="bg-background text-accent px-1 py-0.5 rounded text-xs whitespace-nowrap">.hivemind/</code> folder with settings and conversation history</li>
                        <li>• You can also open external repos - HiveMind will add <code className="bg-background text-accent px-1 py-0.5 rounded text-xs whitespace-nowrap">.hivemind/</code> to them</li>
                        <li>• Changes are saved directly to your file system</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <button className="btn-secondary px-4 py-2" onClick={onClose}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

